// Helpers for the AI Settings pricing tables. The composer's pricing catalog stores a
// model's price in one of two shapes, and both appear in i2vRegistry.json:
//   - nested:  models[modelId] = { "720p": row, "1080p": row, ... }   (Kling, Veo, Seedance, Vidu)
//   - flat:    models[modelId] = row                                   (wan_comfy: a single row,
//              no resolution/mode sub-key — unit "internal", price_usd null)
// flattenPricing() normalises both into a flat list of rows so the table can't trip over the
// flat shape (which previously made the renderer iterate a row's own fields as if they were rows).

export type PriceRow = {
  unit: string;
  price_usd?: string | null;
  price_per_million_units_usd?: string | null;
  credits?: number;
  off_peak_price_usd?: string | null;
  duration_seconds?: number;
  source?: string | null;
};

export type ModelPricing = PriceRow | Record<string, PriceRow>;

export type FlatPriceRow = { modelId: string; variant: string; row: PriceRow };

// A price row carries a billing key; a nested variants map is keyed by resolution/mode and
// never has these keys at the top level — so this reliably tells the two shapes apart.
export function isPriceRow(value: unknown): value is PriceRow {
  return (
    !!value &&
    typeof value === "object" &&
    ("unit" in value ||
      "price_usd" in value ||
      "price_per_million_units_usd" in value)
  );
}

export function flattenPricing(
  models: Record<string, ModelPricing> | undefined,
): FlatPriceRow[] {
  if (!models) return [];
  return Object.entries(models).flatMap(([modelId, value]) => {
    if (isPriceRow(value)) {
      return [{ modelId, variant: "—", row: value }];
    }
    return Object.entries(value)
      .filter(([, row]) => isPriceRow(row))
      .map(([variant, row]) => ({ modelId, variant, row }));
  });
}

const NONE = "—";

// One catalog row -> a human price string, covering every billing unit in the catalog.
export function formatPrice(row: PriceRow): string {
  switch (row.unit) {
    case "second":
      return row.price_usd != null ? `$${row.price_usd} / sec` : NONE;
    case "generation": {
      const clip = row.duration_seconds
        ? ` (${row.duration_seconds}s clip)`
        : "";
      return row.price_usd != null ? `$${row.price_usd} / clip${clip}` : NONE;
    }
    case "video_token":
      return row.price_per_million_units_usd != null
        ? `$${row.price_per_million_units_usd} / 1M tokens`
        : NONE;
    case "internal":
      return "Self-hosted (GPU / runtime)";
    default:
      return row.price_usd != null ? `$${row.price_usd}` : NONE;
  }
}

// Vidu-style extras (credits, off-peak) shown under the price when present.
export function priceExtras(row: PriceRow): string | null {
  const parts: string[] = [];
  if (row.credits != null) parts.push(`${row.credits} credits`);
  if (row.off_peak_price_usd != null) {
    parts.push(`off-peak $${row.off_peak_price_usd}`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}
